import React, { useEffect, useRef } from "react";
import { IonButton, IonSpinner } from "@ionic/react";
import { Controller, FieldValues, SubmitHandler, useForm } from "react-hook-form";
import CommonFormElement from "../../../../components/UI/CommonFormElements/CommonFormElement";
import { CustomPhoneInput } from "../../../../components/CustomPhoneInput/CustomPhoneInput";
import { normalizeLogin } from "../utils";
import { useRequestCallWithSmsFallback } from "../utils/useRequestCallWithSmsFallback";
import { VerificationDateType } from "../types";
import { useTranslation } from "react-i18next";

type PhoneInputFormProps = {
  setIsPhoneVerified: () => void;
  setVerificationData: (data: VerificationDateType) => void;
};

const PhoneInputForm = ({ setIsPhoneVerified, setVerificationData }: PhoneInputFormProps) => {
  const { t } = useTranslation();
  const { requestVerification, isPending, isError } = useRequestCallWithSmsFallback();
  const { handleSubmit, control, watch } = useForm<FieldValues>({
    mode: "onSubmit",
  });

  const handleAuth: SubmitHandler<FieldValues> = (data) => {
    const normalizedPhone = normalizeLogin(data.phone);

    if (!normalizedPhone.length) return;

    requestVerification(normalizedPhone, {
      onSuccess: (verificationResponse, method) => {
        setVerificationData({
          codeId: verificationResponse.verificationCodeId,
          phoneNumber: verificationResponse.phoneNumber,
          method,
        });
        setIsPhoneVerified();
      },
      onError: (error) => {
        console.log(error, "on error");
      },
    });
  };

  const phoneInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    phoneInputRef.current?.focus();
  }, []);

  return (
    <form onSubmit={handleSubmit(handleAuth)} className="flex flex-col items-center w-full">
      <Controller
        name="phone"
        control={control}
        render={({ field: { onChange, value } }) => (
          <CommonFormElement
            isValid={!isError}
            label={t("Phone number")}
            errorMessage={t("Failed to initiate call. Please try again.")}
          >
            <CustomPhoneInput
              autoFocus
              className="border border-gray-200 rounded-md"
              value={value}
              onCustomChange={onChange}
            />
          </CommonFormElement>
        )}
      />
      <IonButton
        fill="clear"
        type="submit"
        disabled={!watch("phone") || watch("phone").length < 11 || isPending}
        className="bg-primary rounded-full mx-5 text-black w-[330px] sm:w-[430px]"
      >
        {t("Get call with code")}
        {isPending && <IonSpinner className="ml-2 size-5 text-white" />}
      </IonButton>
    </form>
  );
};

export { PhoneInputForm };
